import { CityLanguage } from '@/context/languages/languageData';
import { translations, TranslationObject } from '@/context/languages/translations';

export interface TextFragment {
  id: string;
  text: string;
  detectedLanguage: string | null;
  expectedLanguage: CityLanguage;
  isTranslated: boolean;
  isCorrectLanguage: boolean;
  element: string; // button, paragraph, heading, etc.
  filePath: string;
  lineNumber?: number;
  severity: 'critical' | 'high' | 'medium' | 'low';
  suggestion?: string;
}

export interface PageValidationResult {
  pagePath: string;
  expectedLanguage: CityLanguage;
  totalFragments: number;
  translatedFragments: number;
  correctLanguageFragments: number;
  hardcodedTextCount: number;
  translationCoverage: number; // percentage
  languageAccuracy: number; // percentage
  fragments: TextFragment[];
  issues: ValidationIssue[];
  seoIssues: SEOIssue[];
}

export interface ValidationIssue {
  type: 'hardcoded_text' | 'wrong_language' | 'missing_translation' | 'inconsistent_translation';
  severity: 'critical' | 'high' | 'medium' | 'low';
  fragment: TextFragment;
  description: string;
  autoFixable: boolean;
  suggestedFix?: string;
}

export interface SEOIssue {
  type: 'missing_meta_translation' | 'wrong_hreflang' | 'missing_alt_text' | 'incorrect_schema';
  element: string;
  description: string;
  currentValue?: string;
  expectedValue?: string;
}

export interface ProjectValidationReport {
  totalPages: number;
  validatedPages: number;
  overallCoverage: number;
  criticalIssues: number;
  totalIssues: number;
  pageResults: PageValidationResult[];
  languageStatistics: Record<CityLanguage, {
    pageCount: number;
    avgCoverage: number;
    avgAccuracy: number;
  }>;
  topIssues: ValidationIssue[];
  recommendations: string[];
}

export class TranslationValidationEngine {
  private static readonly HARDCODED_PATTERNS = [
    // English patterns
    /(^|[^a-zA-Z])(The|And|Or|But|With|For|In|On|At|By|To|From|About|Through|During|Before|After|Above|Below|Up|Down|Out|Off|Over|Under|Again|Further|Then|Once|Here|There|Where|When|Why|How|All|Any|Both|Each|Few|More|Most|Other|Some|Such|No|Nor|Not|Only|Own|Same|So|Than|Too|Very|Can|Will|Just|Should|Now|Get|Go|Come|See|Know|Take|Give|Find|Tell|Ask|Try|Feel|Leave|Put|Use|Work|Call|Make|Move|Live|Believe|Bring|Happen|Write|Provide|Sit|Stand|Lose|Pay|Meet|Include|Continue|Set|Learn|Change|Lead|Understand|Watch|Follow|Stop|Create|Speak|Read|Allow|Add|Spend|Grow|Open|Walk|Win|Offer|Remember|Love|Consider|Appear|Buy|Wait|Serve|Die|Send|Expect|Build|Stay|Fall|Cut|Reach|Kill|Remain)(\s|[^a-zA-Z]|$)/gi,
    
    // Common UI text patterns
    /(Sign up|Sign in|Log in|Log out|Submit|Cancel|Save|Edit|Delete|Search|Filter|Sort|Next|Previous|Back|Continue|Finish|Start|Begin|End|Close|Open|View|Show|Hide|More|Less|All|None|Yes|No|OK|Loading|Error|Success|Warning|Info|Help|Contact|Support|FAQ|Terms|Privacy|Cookie|Settings|Profile|Account|Dashboard|Home|About|Services|Products|Blog|News|Events|Careers|Team|Company|Mission|Vision|Values)/gi,
    
    // Numbers and common words that should be translated
    /(Monday|Tuesday|Wednesday|Thursday|Friday|Saturday|Sunday|January|February|March|April|May|June|July|August|September|October|November|December|Today|Tomorrow|Yesterday|Week|Month|Year|Hour|Minute|Second|Morning|Afternoon|Evening|Night)/gi,
    
    // Mental health specific terms
    /(Therapist|Therapy|Psychologist|Psychology|Psychiatrist|Psychiatry|Counselor|Counseling|Mental Health|Depression|Anxiety|Stress|Trauma|PTSD|Addiction|Recovery|Treatment|Session|Appointment|Consultation|Assessment|Diagnosis|Medication|Wellness|Mindfulness|CBT|DBT|EMDR|Group Therapy|Individual Therapy|Family Therapy|Couples Therapy)/gi
  ];

  private static readonly LANGUAGE_DETECTION_PATTERNS: Record<string, RegExp[]> = {
    'en': [
      /\b(the|and|or|but|with|for|in|on|at|by|to|from|about|through|during|before|after|therapy|therapist|psychologist|psychology|mental health|depression|anxiety|stress|treatment|session)\b/gi
    ],
    'es': [
      /\b(el|la|los|las|y|o|pero|con|para|en|sobre|de|desde|acerca de|terapia|terapeuta|psicólogo|psicología|salud mental|depresión|ansiedad|estrés|tratamiento|sesión)\b/gi
    ],
    'fr': [
      /\b(le|la|les|et|ou|mais|avec|pour|dans|sur|de|depuis|à propos de|thérapie|thérapeute|psychologue|psychologie|santé mentale|dépression|anxiété|stress|traitement|séance)\b/gi
    ],
    'de': [
      /\b(der|die|das|und|oder|aber|mit|für|in|auf|von|seit|über|therapie|therapeut|psychologe|psychologie|geistige gesundheit|depression|angst|stress|behandlung|sitzung)\b/gi
    ],
    'it': [
      /\b(il|la|lo|i|gli|le|e|o|ma|con|per|in|su|di|da|circa|terapia|terapeuta|psicologo|psicologia|salute mentale|depressione|ansia|stress|trattamento|sessione)\b/gi
    ],
    'pt': [
      /\b(o|a|os|as|e|ou|mas|com|para|em|sobre|de|desde|acerca de|terapia|terapeuta|psicólogo|psicologia|saúde mental|depressão|ansiedade|estresse|tratamento|sessão)\b/gi
    ]
  };

  static analyzeTextFragment(
    text: string,
    expectedLanguage: CityLanguage,
    context: {
      filePath: string;
      element: string;
      lineNumber?: number;
    }
  ): TextFragment {
    const detectedLanguage = this.detectLanguage(text);
    const isTranslated = this.isTextTranslated(text);
    const isCorrectLanguage = detectedLanguage === expectedLanguage;
    const severity = this.calculateSeverity(text, isTranslated, isCorrectLanguage, context.element);

    return {
      id: `${context.filePath}:${context.lineNumber || 'unknown'}:${text.substring(0, 20)}`,
      text,
      detectedLanguage,
      expectedLanguage,
      isTranslated,
      isCorrectLanguage,
      element: context.element,
      filePath: context.filePath,
      lineNumber: context.lineNumber,
      severity,
      suggestion: this.generateSuggestion(text, expectedLanguage, isTranslated)
    };
  }

  static detectLanguage(text: string): string | null {
    if (!text || text.trim().length === 0) return null;
    
    // Skip very short text or numbers
    if (text.trim().length < 3 || /^\d+$/.test(text.trim())) return null;
    
    const scores: Record<string, number> = {};
    
    // Check against each language pattern
    for (const [lang, patterns] of Object.entries(this.LANGUAGE_DETECTION_PATTERNS)) {
      scores[lang] = 0;
      for (const pattern of patterns) {
        const matches = text.match(pattern);
        if (matches) {
          scores[lang] += matches.length;
        }
      }
    }
    
    // Find the language with the highest score
    const maxScore = Math.max(...Object.values(scores));
    if (maxScore === 0) return null;
    
    const detectedLang = Object.entries(scores).find(([_, score]) => score === maxScore)?.[0];
    return detectedLang || null;
  }

  static isTextTranslated(text: string): boolean {
    // Check if text exists in translations object
    for (const [key, translationObj] of Object.entries(translations)) {
      for (const translation of Object.values(translationObj)) {
        if (typeof translation === 'string' && translation.toLowerCase() === text.toLowerCase()) {
          return true;
        }
      }
    }
    
    // Check for common hardcoded patterns
    for (const pattern of this.HARDCODED_PATTERNS) {
      if (pattern.test(text)) {
        return false;
      }
    }
    
    return true; // Assume translated if not caught by patterns
  }

  static calculateSeverity(
    text: string,
    isTranslated: boolean,
    isCorrectLanguage: boolean,
    element: string
  ): 'critical' | 'high' | 'medium' | 'low' {
    // Critical: Hardcoded text in high-visibility elements
    if (!isTranslated && ['button', 'heading', 'title', 'meta'].includes(element)) {
      return 'critical';
    }
    
    // High: Wrong language in important elements
    if (!isCorrectLanguage && ['button', 'heading', 'navigation'].includes(element)) {
      return 'high';
    }
    
    // Medium: Hardcoded text in regular content
    if (!isTranslated) {
      return 'medium';
    }
    
    // Low: Wrong language in less important elements
    if (!isCorrectLanguage) {
      return 'low';
    }
    
    return 'low';
  }

  static generateSuggestion(
    text: string,
    expectedLanguage: CityLanguage,
    isTranslated: boolean
  ): string | undefined {
    if (!isTranslated) {
      return `Create translation key for: "${text}" and translate to ${expectedLanguage}`;
    }
    
    return `Ensure this text is properly translated to ${expectedLanguage}`;
  }

  static extractRouteLanguage(routePath: string): CityLanguage {
    // Root path is always English
    if (routePath === '/' || routePath === '') {
      return 'en';
    }
    
    // Extract language from path like /es/madrid, /fr/paris, etc.
    const match = routePath.match(/^\/([a-z]{2})(?:\/|$)/);
    if (match) {
      const lang = match[1];
      // Check if it's a supported language
      const supportedLanguages: CityLanguage[] = [
        'en', 'es', 'fr', 'de', 'it', 'pt', 'ko', 'ja', 'zh', 'ar', 'hi', 'ru', 'tr', 'th', 'vi', 'id', 'nl', 'ur', 'bn', 'fa', 'ms'
      ];
      if (supportedLanguages.includes(lang as CityLanguage)) {
        return lang as CityLanguage;
      }
    }
    
    return 'en'; // Default to English
  }

  static generateValidationIssues(fragments: TextFragment[]): ValidationIssue[] {
    const issues: ValidationIssue[] = [];
    
    for (const fragment of fragments) {
      if (!fragment.isTranslated) {
        issues.push({
          type: 'hardcoded_text',
          severity: fragment.severity,
          fragment,
          description: `Hardcoded text found: "${fragment.text}"`,
          autoFixable: true,
          suggestedFix: `Replace with translation key: t('${this.generateTranslationKey(fragment.text)}')`
        });
      }
      
      if (!fragment.isCorrectLanguage && fragment.detectedLanguage) {
        issues.push({
          type: 'wrong_language',
          severity: fragment.severity,
          fragment,
          description: `Text is in ${fragment.detectedLanguage} but should be in ${fragment.expectedLanguage}`,
          autoFixable: false,
          suggestedFix: `Translate this text to ${fragment.expectedLanguage}`
        });
      }
    }
    
    return issues;
  }

  static generateTranslationKey(text: string): string {
    return text
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, '')
      .replace(/\s+/g, '_')
      .substring(0, 50);
  }
}